/* WYRO theme behaviour. Progressive: every feature degrades to working HTML without JS. */
(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document.documentElement.classList.add("js");

  /* Scroll reveal */
  const revealIo = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add("is-in");
          revealIo.unobserve(e.target);
        }
      }
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
  );
  document.querySelectorAll("[data-reveal]").forEach((el) => revealIo.observe(el));

  /* Header: solid background once scrolled */
  const header = document.querySelector("[data-header]");
  if (header) {
    const onScroll = () => {
      const s = window.scrollY > 8;
      header.classList.toggle("border-line", s);
      header.classList.toggle("bg-ink/85", s);
      header.classList.toggle("backdrop-blur-md", s);
      header.classList.toggle("border-transparent", !s);
      header.classList.toggle("bg-ink", !s);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* Mobile menu (native <dialog>) */
  const menu = document.querySelector("[data-menu]");
  document.querySelector("[data-menu-open]")?.addEventListener("click", () => menu?.showModal());
  menu?.querySelector("[data-menu-close]")?.addEventListener("click", () => menu.close());
  menu?.addEventListener("click", (e) => {
    if (e.target === menu || e.target.closest("a")) menu.close();
  });

  /* Demo videos: load near the viewport, play while visible, never with sound. */
  document.querySelectorAll("[data-demo]").forEach((fig) => {
    const v = fig.querySelector("video");
    const toggle = fig.querySelector("[data-toggle]");
    const replay = fig.querySelector("[data-replay]");
    const label = fig.querySelector("[data-label]");
    const icon = fig.querySelector("[data-icon]");
    const title = (v.getAttribute("aria-label") || "").split(":")[0];
    let userPaused = false;
    let loaded = false;
    let visible = false;
    const wantsPlay = () => visible && !userPaused && !reduceMotion;
    const tryPlay = () => {
      if (wantsPlay() && v.paused) v.play().catch(() => {});
    };
    // Start as soon as there is enough data — play() sent before that can be dropped on mobile.
    v.addEventListener("loadeddata", tryPlay);
    v.addEventListener("canplay", tryPlay);

    const load = () => {
      if (loaded) return;
      loaded = true;
      v.poster = v.dataset.poster;
      const s = v.querySelector("source");
      s.src = s.dataset.src;
      v.load();
    };
    const sync = () => {
      const playing = !v.paused;
      if (label) label.textContent = playing ? "Pause" : "Play";
      if (icon) icon.textContent = playing ? "❚❚" : "▶";
      toggle?.setAttribute("aria-label", `${playing ? "Pause" : "Play"} ${title}`);
    };
    v.addEventListener("play", sync);
    v.addEventListener("pause", sync);
    v.addEventListener("error", () => {
      v.poster = v.dataset.poster;
    });

    toggle?.addEventListener("click", () => {
      load();
      if (v.paused) {
        userPaused = false;
        v.play().catch(() => {});
      } else {
        userPaused = true;
        v.pause();
      }
    });
    replay?.addEventListener("click", () => {
      load();
      userPaused = false;
      v.currentTime = 0;
      v.play().catch(() => {});
    });

    const start = () => {
      new IntersectionObserver(
        ([e], io) => {
          if (e.isIntersecting) {
            load();
            io.disconnect();
          }
        },
        { rootMargin: "600px 0px" },
      ).observe(v);
      new IntersectionObserver(
        ([e]) => {
          visible = e.isIntersecting;
          if (visible) {
            load();
            tryPlay();
          } else {
            v.pause();
          }
        },
        { threshold: 0.35 },
      ).observe(v);
    };
    // Above-the-fold clips wait for page load so they never compete with the main image.
    let started = false;
    const startOnce = () => {
      if (started) return;
      started = true;
      start();
    };
    if (fig.hasAttribute("data-priority") && document.readyState !== "complete") {
      window.addEventListener("load", startOnce, { once: true });
      setTimeout(startOnce, 2500); // don't let a slow resource block the hero clip on mobile
    } else {
      startOnce();
    }
  });

  /* Product card tap toggle (touch devices) */
  document.querySelectorAll("[data-detail-toggle]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const card = btn.closest("[data-card]");
      const on = !card.hasAttribute("data-detail");
      card.toggleAttribute("data-detail", on);
      btn.setAttribute("aria-pressed", String(on));
      btn.textContent = on ? "Full view" : "See the pattern";
    });
  });

  /* Product gallery views (crops of the same image) */
  const gallery = document.querySelector("[data-gallery]");
  if (gallery) {
    const main = gallery.querySelector(".js-gallery-main");
    const lbl = gallery.querySelector("[data-gallery-label]");
    gallery.querySelectorAll("[data-view]").forEach((btn) => {
      btn.addEventListener("click", () => {
        gallery.querySelectorAll("[data-view]").forEach((b) => b.setAttribute("aria-pressed", String(b === btn)));
        const scale = btn.dataset.scale;
        if (main) {
          main.style.transformOrigin = btn.dataset.origin;
          main.style.transform = `scale(${scale})`;
        }
        if (lbl) lbl.textContent = btn.dataset.label + (scale !== "1" ? " · detail" : "");
      });
    });
  }

  /* Size selector → free pre-order reservation (tags + note on the customer record) */
  const reserveForm = document.querySelector("[data-reserve] form");
  if (sizes0() && reserveForm) {
    const tags = reserveForm.querySelector("[data-tags]");
    const note = reserveForm.querySelector("[data-note]");
    const submit = reserveForm.querySelector("[data-submit]");
    sizes0().querySelectorAll("[data-variant]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sizes0().querySelectorAll("[data-variant]").forEach((b) => b.setAttribute("aria-checked", String(b === btn)));
        const size = btn.textContent.trim();
        const slug = size.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        tags.value = `${tags.dataset.base},preorder-${tags.dataset.handle}-${slug}`;
        note.value = `Pre-order: ${note.dataset.product} — size ${size}`;
        reserveForm.dataset.size = size;
        submit.disabled = false;
        submit.textContent = `Pre-order size ${size} — pay later`;
      });
    });
  }
  function sizes0() {
    return document.querySelector("[data-sizes]");
  }

  /* Size selector → variant id */
  const sizes = document.querySelector("[data-sizes]");
  const form = document.querySelector("[data-product-form]");
  if (sizes && form) {
    const input = form.querySelector("[data-variant-input]");
    const submit = form.querySelector("[data-submit]");
    const price = document.querySelector("[data-price]");
    sizes.querySelectorAll("[data-variant]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sizes.querySelectorAll("[data-variant]").forEach((b) => b.setAttribute("aria-checked", String(b === btn)));
        input.value = btn.dataset.variant;
        submit.disabled = false;
        submit.textContent = `Pre-order — ${btn.dataset.price}`;
        if (price) price.textContent = btn.dataset.price;
      });
    });
  }

  /* Mobile buy bar: shown once the main action scrolls away */
  const bar = document.querySelector("[data-buybar]");
  const reserve = document.getElementById("reserve");
  if (bar && reserve) {
    new IntersectionObserver(([e]) => {
      const show = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle("translate-y-full", !show);
      bar.inert = !show;
      bar.setAttribute("aria-hidden", String(!show));
    }).observe(reserve);
  }

  /* Pre-order notification to the store: a Shopify contact-form submission, which Shopify emails to
     the store's customer email address. Sent before the reservation itself so it also goes out when
     the reservation continues through Shopify's CAPTCHA page. Best effort — the customer record is
     the source of truth. */
  function notifyStore(f) {
    const val = (n) => f.querySelector(`[name="contact[${n}]"]`)?.value.trim() || "";
    const product = f.querySelector("[data-note]")?.dataset.product || "";
    const size = f.dataset.size || "";
    const name = `${val("first_name")} ${val("last_name")}`.trim();
    const fd = new FormData();
    fd.append("form_type", "contact");
    fd.append("utf8", "✓");
    fd.append("contact[email]", val("email"));
    fd.append("contact[name]", name);
    fd.append("contact[Product]", product);
    fd.append("contact[Size]", size);
    fd.append("contact[body]", `New WYRO pre-order\n\nName: ${name}\nEmail: ${val("email")}\nProduct: ${product}\nSize: ${size}\nPage: ${location.href}`);
    try {
      fetch("/contact#contact_form", { method: "POST", body: fd, keepalive: true }).catch(() => {});
    } catch {}
  }

  /* Sign-up forms: require consent, submit in place, fall back to a normal post for CAPTCHA. */
  document.querySelectorAll("[data-signup] form").forEach((f) => {
    const err = f.querySelector("[data-error]");
    const showError = (msg) => {
      if (!err) return;
      err.textContent = msg;
      err.classList.remove("hidden");
    };
    f.addEventListener("submit", async (e) => {
      const email = f.querySelector('input[type="email"]');
      const consent = f.querySelector("[data-consent]");
      const missingName = [...f.querySelectorAll('input[type="text"][required]')].some((i) => !i.value.trim());
      if (f.closest("[data-reserve]") && !f.dataset.size) {
        e.preventDefault();
        return showError("Please choose your size first.");
      }
      if (missingName) {
        e.preventDefault();
        return showError("Please enter your first and last name.");
      }
      if (!email.value || !email.checkValidity()) {
        e.preventDefault();
        return showError("Please enter a valid email address.");
      }
      if (consent && !consent.checked) {
        e.preventDefault();
        return showError("Please confirm you agree to receive emails.");
      }
      e.preventDefault();
      if (f.closest("[data-reserve]")) notifyStore(f);
      const btn = f.querySelector('button[type="submit"]');
      btn.disabled = true;
      btn.textContent = "Sending…";
      try {
        const res = await fetch(f.action, { method: "POST", body: new FormData(f) });
        if (res.url.includes("/challenge")) return f.submit();
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const fresh = doc.getElementById(f.id);
        if (res.ok && res.url.includes("customer_posted=true") && fresh) {
          f.replaceWith(fresh);
        } else if (fresh?.querySelector("[data-error]:not(.hidden)")) {
          f.replaceWith(fresh);
        } else {
          f.submit();
        }
      } catch {
        f.submit();
      }
    });
  });
})();
